<script setup lang="ts">
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { Bell, CheckCheck } from 'lucide-vue-next';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { REMIND_DAYS_OPTIONS, type RemindDays } from '@arpm/shared/constants';

import type {
  NotificationCountDto,
  NotificationDto,
  NotificationListDto,
  PreferencesDto,
} from '@/api/generated/models/index.js';

import { meControllerMyPreferencesV1, meControllerUpdatePreferencesV1 } from '@/api/generated/me/me.js';
import {
  notificationsControllerListV1,
  notificationsControllerMarkReadV1,
  notificationsControllerUnreadCountV1,
} from '@/api/generated/notifications/notifications.js';
import { setAppBadge, usePush } from '@/composables/usePush.js';
import { useAuthStore } from '@/stores/auth.js';

/**
 * Колокольчик в шапке: счётчик непрочитанных (опрос раз в минуту) и лента
 * при открытии. Клик по уведомлению отмечает его прочитанным и ведёт по ссылке.
 */
const { t } = useI18n();
const router = useRouter();
const qc = useQueryClient();
const auth = useAuthStore();

const enabled = computed(() => auth.isAuthenticated && Boolean(auth.user?.employeeId));

/** За сколько дней напоминать о сроке поручения — личная настройка (F1). */
const { data: prefs } = useQuery({
  queryKey: ['me', 'preferences'],
  queryFn: () => meControllerMyPreferencesV1() as unknown as Promise<PreferencesDto>,
  enabled,
});
const prefMutation = useMutation({
  mutationFn: (days: RemindDays) =>
    meControllerUpdatePreferencesV1({ remindDaysBefore: days }) as unknown as Promise<PreferencesDto>,
  onSuccess: (next) => qc.setQueryData(['me', 'preferences'], next),
});
function remindLabel(d: number): string {
  if (d === 0) return t('notifications.remindSameDay');
  // Варианты — 1, 2, 3, 5, 7: хватает трёх форм без общих правил склонения.
  const form = d === 1 ? 'one' : d < 5 ? 'few' : 'many';
  return t(`notifications.remindDays.${form}`, { n: d });
}
const { data: count } = useQuery({
  queryKey: ['notifications', 'count'],
  queryFn: () => notificationsControllerUnreadCountV1() as unknown as Promise<NotificationCountDto>,
  enabled,
  refetchInterval: 60_000,
});
const unread = computed(() => count.value?.unread ?? 0);

/** Число на иконке приложения (экран «Домой», док) — как на колокольчике. */
watch(unread, (n) => setAppBadge(n));

/** Пуш на этом устройстве: состояние узнаём при входе (заодно привязываем подписку) и при открытии. */
const push = usePush();
watch(enabled, (on) => on && void push.refresh(), { immediate: true });
const pushError = ref('');
async function togglePush() {
  pushError.value = '';
  try {
    if (push.state.value === 'on') await push.disable();
    else await push.enable();
  } catch {
    pushError.value = t('notifications.push.error');
  }
}

const open = ref(false);
const { data: list, isLoading } = useQuery({
  queryKey: ['notifications', 'list'],
  queryFn: () => notificationsControllerListV1({ take: 30 }) as unknown as Promise<NotificationListDto>,
  enabled: computed(() => enabled.value && open.value),
});

function refresh() {
  void qc.invalidateQueries({ queryKey: ['notifications'] });
  // Уведомление почти всегда значит, что поменялось что-то в поручениях.
  void qc.invalidateQueries({ queryKey: ['assignments'] });
}

function toggle() {
  open.value = !open.value;
  if (open.value) refresh();
}

async function go(n: NotificationDto) {
  open.value = false;
  if (!n.readAt) {
    await notificationsControllerMarkReadV1({ ids: [n.id] }).catch(() => undefined);
    refresh();
  }
  if (n.link) void router.push(n.link);
}

async function readAll() {
  await notificationsControllerMarkReadV1({}).catch(() => undefined);
  refresh();
}

const timeFmt = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });
const dateFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });
function when(iso: string): string {
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60_000);
  if (mins < 1) return t('notifications.justNow');
  if (mins < 60) return t('notifications.minutesAgo', { n: mins });
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return timeFmt.format(d);
  const y = new Date(today.getTime() - 86_400_000);
  if (d.toDateString() === y.toDateString()) return t('notifications.yesterday', { time: timeFmt.format(d) });
  return dateFmt.format(d);
}

// Закрытие по клику мимо и Escape.
const root = ref<HTMLElement | null>(null);
function onDocClick(e: MouseEvent) {
  if (open.value && root.value && !root.value.contains(e.target as Node)) open.value = false;
}
function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') open.value = false;
}
onMounted(() => {
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick);
  document.removeEventListener('keydown', onKey);
});
</script>

<template>
  <div v-if="enabled" ref="root" class="bell">
    <button
      type="button"
      class="bell__trigger"
      :aria-label="unread ? t('notifications.ariaUnread', { n: unread }) : t('notifications.title')"
      :aria-expanded="open"
      @click="toggle"
    >
      <Bell :size="18" :stroke-width="1.75" />
      <span v-if="unread" class="bell__count">{{ unread > 99 ? '99+' : unread }}</span>
    </button>

    <div v-if="open"
class="pop"
role="dialog"
:aria-label="t('notifications.title')">
      <header class="pop__head">
        <b>{{ t('notifications.title') }}</b>
        <button v-if="unread"
type="button"
class="pop__readall"
@click="readAll">
          <CheckCheck :size="14" :stroke-width="2" />
          {{ t('notifications.readAll') }}
        </button>
      </header>
      <p v-if="isLoading" class="pop__empty">{{ t('common.loading') }}</p>
      <p v-else-if="!list?.items.length" class="pop__empty">{{ t('notifications.empty') }}</p>
      <ul v-else class="pop__list">
        <li v-for="n in list.items" :key="n.id">
          <button type="button"
class="item"
:class="{ 'item--read': n.readAt }"
@click="go(n)">
            <span class="item__text">
              <span class="item__title">{{ n.title }}</span>
              <span v-if="n.body" class="item__body">{{ n.body }}</span>
              <span class="item__time">{{ when(n.createdAt) }}</span>
            </span>
            <span v-if="!n.readAt" class="item__dot" aria-hidden="true" />
          </button>
        </li>
      </ul>
      <div v-if="push.state.value !== 'unknown' && push.state.value !== 'server-off'" class="push">
        <template v-if="push.state.value === 'on' || push.state.value === 'off'">
          <span class="push__text">{{ t(`notifications.push.${push.state.value}`) }}</span>
          <button type="button" class="push__btn" :disabled="push.busy.value" @click="togglePush">
            {{ push.state.value === 'on' ? t('notifications.push.disable') : t('notifications.push.enable') }}
          </button>
        </template>
        <span v-else class="push__text push__text--hint">{{ t(`notifications.push.${push.state.value}`) }}</span>
        <span v-if="pushError" class="push__error" role="alert">{{ pushError }}</span>
      </div>
      <footer class="pop__foot">
        <label v-if="prefs" class="remind">
          <span>{{ t('notifications.remindLabel') }}</span>
          <select
            class="remind__select"
            :value="prefs.remindDaysBefore"
            :disabled="prefMutation.isPending.value"
            @change="prefMutation.mutate(Number(($event.target as HTMLSelectElement).value) as RemindDays)"
          >
            <option v-for="d in REMIND_DAYS_OPTIONS" :key="d" :value="d">{{ remindLabel(d) }}</option>
          </select>
        </label>
        <span>{{ t('notifications.emailNote') }}</span>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.bell { position: relative; }
.bell__trigger {
  position: relative;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: background var(--transition-fast), border-color var(--transition-fast);
}
.bell__trigger:hover,
.bell__trigger[aria-expanded='true'] { background: var(--color-bg-soft); border-color: var(--color-border); }
.bell__count {
  position: absolute;
  top: 2px;
  right: 1px;
  display: grid;
  place-items: center;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  border: 2px solid var(--color-bg);
  border-radius: var(--radius-full);
  background: var(--color-danger);
  color: #fff;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.pop {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: var(--z-dropdown);
  width: 380px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-bg);
  box-shadow: var(--shadow-lg);
}
.pop__head { display: flex; align-items: center; justify-content: space-between; padding: var(--space-3) var(--space-4); border-bottom: 1px solid var(--color-border); }
.pop__readall { display: inline-flex; align-items: center; gap: 4px; padding: 0; border: 0; background: none; color: var(--color-primary); font: inherit; font-size: var(--font-size-sm); cursor: pointer; }
.pop__empty { margin: 0; padding: var(--space-6) var(--space-4); text-align: center; color: var(--color-text-muted); font-size: var(--font-size-sm); }
.push { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2); padding: var(--space-2) var(--space-4); border-top: 1px solid var(--color-border); font-size: var(--font-size-xs); color: var(--color-text-secondary); }
.push__text { flex: 1; min-width: 160px; }
.push__text--hint { color: var(--color-text-muted); }
.push__btn { padding: 4px 10px; border: 1px solid var(--color-primary); border-radius: var(--radius-sm); background: none; color: var(--color-primary); font: inherit; font-weight: var(--font-weight-semibold); cursor: pointer; }
.push__btn:disabled { opacity: 0.6; cursor: progress; }
.push__error { width: 100%; color: var(--color-danger); }
.remind { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2); margin-bottom: var(--space-2); color: var(--color-text-secondary); }
.remind__select { height: 28px; padding: 0 var(--space-2); border: 1px solid var(--color-border); border-radius: var(--radius-sm); background: var(--color-bg); color: var(--color-text); font: inherit; }
.pop__list { max-height: 420px; margin: 0; padding: 0; overflow-y: auto; list-style: none; }
.item {
  display: flex;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-3) var(--space-4);
  border: 0;
  border-bottom: 1px solid var(--color-border-subtle);
  background: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.item:hover { background: var(--color-bg-surface); }
.item__text { display: flex; flex: 1; flex-direction: column; gap: 2px; min-width: 0; }
.item__title { font-size: var(--font-size-sm); font-weight: var(--font-weight-semibold); line-height: 1.35; }
.item--read .item__title { font-weight: var(--font-weight-normal); color: var(--color-text-secondary); }
.item__body { display: -webkit-box; overflow: hidden; -webkit-box-orient: vertical; -webkit-line-clamp: 2; white-space: pre-line; font-size: var(--font-size-xs); color: var(--color-text-muted); }
.item__time { font-size: var(--font-size-xs); color: var(--color-text-placeholder); }
.item__dot { flex: none; width: 8px; height: 8px; margin-top: 6px; border-radius: 50%; background: var(--color-primary); }
.pop__foot { padding: var(--space-2) var(--space-4); font-size: var(--font-size-xs); color: var(--color-text-muted); background: var(--color-bg-surface); }
@media (max-width: 720px) {
  .pop { position: fixed; top: 60px; right: 12px; left: 12px; width: auto; max-width: none; }
}
</style>
